import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { format } from "date-fns";

interface PendingRegistration {
  id: string;
  tournamentId: string;
  player1Id: string;
  player2Id: string;
  player1Name: string;
  player2Name: string;
  player1Email?: string;
  player2Email?: string;
  player1Level?: string;
  player2Level?: string;
  teamName: string;
  player1IsNew: boolean;
  player2IsNew: boolean;
  registeredAt: Date;
  pendingReason: string;
  tournament?: {
    id: string;
    name: string;
    startDate: string;
  };
}

interface WaitlistRegistration {
  id: string;
  tournamentId: string;
  player1Id: string;
  player2Id: string;
  player1Name: string;
  player2Name: string;
  teamName: string;
  registeredAt: Date;
  spotsAvailable: number | null;
  tournament?: { id: string; name: string; startDate: string };
}

export default function TournamentRegistrationRequests() {
  const [registrations, setRegistrations] = useState<PendingRegistration[]>([]);
  const [waitlist, setWaitlist] = useState<WaitlistRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] =
    useState<PendingRegistration | null>(null);
  const [player1Username, setPlayer1Username] = useState("");
  const [player2Username, setPlayer2Username] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [confirmingWaitlistId, setConfirmingWaitlistId] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingRegistrations();
    fetchWaitlist();
  }, []);

  const fetchPendingRegistrations = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiRequest(
        "/api/tournaments/pending-registrations",
      );
      if (response.ok) {
        const data = await response.json();
        setRegistrations(
          data.map((reg: any) => ({
            ...reg,
            registeredAt: reg.registeredAt
              ? new Date(reg.registeredAt)
              : new Date(),
          })),
        );
      } else {
        setError("Failed to load pending registrations");
      }
    } catch (err) {
      console.error("Error fetching pending registrations:", err);
      setError("Failed to load pending registrations");
    } finally {
      setLoading(false);
    }
  };

  const fetchWaitlist = async () => {
    try {
      const response = await apiRequest("/api/tournaments/waitlist-registrations");
      if (response.ok) {
        const data = await response.json();
        setWaitlist(
          data.map((reg: any) => ({
            ...reg,
            registeredAt: reg.registeredAt ? new Date(reg.registeredAt) : new Date(),
          })),
        );
      }
    } catch (err) {
      console.error("Error fetching waitlist:", err);
    }
  };

  const handleConfirmWaitlist = async (registrationId: string) => {
    setConfirmingWaitlistId(registrationId);
    setError("");
    try {
      const response = await apiRequest(
        `/api/tournaments/confirm-waitlist/${registrationId}`,
        { method: "POST" },
      );
      if (response.ok) {
        fetchWaitlist();
      } else {
        const data = await response.json();
        setError(data.error || "Failed to confirm waitlist registration");
      }
    } catch (err) {
      setError("Failed to confirm waitlist registration");
    } finally {
      setConfirmingWaitlistId(null);
    }
  };

  const handleApproveClick = (registration: PendingRegistration) => {
    setSelectedRegistration(registration);
    // Auto-generate usernames from email (part before @)
    if (registration.player1IsNew && registration.player1Email) {
      setPlayer1Username(registration.player1Email.split("@")[0]);
    }
    if (registration.player2IsNew && registration.player2Email) {
      setPlayer2Username(registration.player2Email.split("@")[0]);
    }
    setApproveDialogOpen(true);
  };

  const handleRejectClick = (registration: PendingRegistration) => {
    setSelectedRegistration(registration);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedRegistration) return;

    // Validate usernames for new players
    if (selectedRegistration.player1IsNew && !player1Username.trim()) {
      setError("Player 1 username is required");
      return;
    }
    if (selectedRegistration.player2IsNew && !player2Username.trim()) {
      setError("Player 2 username is required");
      return;
    }

    try {
      setProcessing(true);
      setError("");
      const response = await apiRequest(
        `/api/tournaments/approve-registration/${selectedRegistration.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            player1Username: selectedRegistration.player1IsNew
              ? player1Username
              : undefined,
            player2Username: selectedRegistration.player2IsNew
              ? player2Username
              : undefined,
          }),
        },
      );

      if (response.ok) {
        setApproveDialogOpen(false);
        setSelectedRegistration(null);
        setPlayer1Username("");
        setPlayer2Username("");
        fetchPendingRegistrations(); // Refresh list
      } else {
        const data = await response.json();
        setError(data.error || "Failed to approve registration");
      }
    } catch (err) {
      console.error("Error approving registration:", err);
      setError("Failed to approve registration");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRegistration) return;

    try {
      setProcessing(true);
      setError("");
      const response = await apiRequest(
        `/api/tournaments/reject-registration/${selectedRegistration.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rejectionReason }),
        },
      );

      if (response.ok) {
        setRejectDialogOpen(false);
        setSelectedRegistration(null);
        setRejectionReason("");
        fetchPendingRegistrations(); // Refresh list
      } else {
        const data = await response.json();
        setError(data.error || "Failed to reject registration");
      }
    } catch (err) {
      console.error("Error rejecting registration:", err);
      setError("Failed to reject registration");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Tournament Registration Requests
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and approve new player accounts for tournament registrations.
        Sorted by newest requests first.
      </Typography>

      {registrations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No pending registration requests
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Submitted</TableCell>
                <TableCell>Tournament</TableCell>
                <TableCell>Team Name</TableCell>
                <TableCell>Player 1</TableCell>
                <TableCell>Player 2</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {registrations.map((registration) => (
                <TableRow key={registration.id}>
                  <TableCell>
                    {format(registration.registeredAt, "MMM d, yyyy HH:mm")}
                  </TableCell>
                  <TableCell>
                    {registration.tournament?.name || "Unknown Tournament"}
                  </TableCell>
                  <TableCell>{registration.teamName}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {registration.player1Name}
                      </Typography>
                      {registration.player1IsNew && (
                        <Chip
                          label="New Player"
                          size="small"
                          color="warning"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                      {registration.player1Email && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {registration.player1Email}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {registration.player2Name}
                      </Typography>
                      {registration.player2IsNew && (
                        <Chip
                          label="New Player"
                          size="small"
                          color="warning"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                      {registration.player2Email && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {registration.player2Email}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label="Pending Approval"
                      color="warning"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={() => handleApproveClick(registration)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Cancel />}
                        onClick={() => handleRejectClick(registration)}
                      >
                        Reject
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Waitlist Section */}
      <Typography variant="h5" sx={{ mt: 5, mb: 1 }}>
        Waitlist
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Teams registered after the tournament reached capacity. Confirm them when a spot opens up — they are shown oldest first.
      </Typography>
      {waitlist.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center", mb: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No teams on the waitlist
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Registered</TableCell>
                <TableCell>Tournament</TableCell>
                <TableCell>Team Name</TableCell>
                <TableCell>Player 1</TableCell>
                <TableCell>Player 2</TableCell>
                <TableCell>Spots Available</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {waitlist.map((reg) => (
                <TableRow key={reg.id}>
                  <TableCell>{format(reg.registeredAt, "MMM d, yyyy HH:mm")}</TableCell>
                  <TableCell>{reg.tournament?.name || "Unknown"}</TableCell>
                  <TableCell>{reg.teamName}</TableCell>
                  <TableCell>{reg.player1Name}</TableCell>
                  <TableCell>{reg.player2Name}</TableCell>
                  <TableCell>
                    {reg.spotsAvailable !== null ? (
                      <Chip
                        label={reg.spotsAvailable > 0 ? `${reg.spotsAvailable} open` : "Full"}
                        color={reg.spotsAvailable > 0 ? "success" : "default"}
                        size="small"
                      />
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      disabled={
                        confirmingWaitlistId === reg.id ||
                        reg.spotsAvailable !== null && reg.spotsAvailable <= 0
                      }
                      startIcon={confirmingWaitlistId === reg.id ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
                      onClick={() => handleConfirmWaitlist(reg.id)}
                    >
                      Confirm
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !processing && setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review and create usernames for new players
          </Typography>

          {selectedRegistration?.player1IsNew && (
            <TextField
              fullWidth
              label={`Username for ${selectedRegistration.player1Name}`}
              value={player1Username}
              onChange={(e) => setPlayer1Username(e.target.value)}
              sx={{ mb: 2 }}
              helperText={`Email: ${selectedRegistration.player1Email || "N/A"}`}
            />
          )}

          {selectedRegistration?.player2IsNew && (
            <TextField
              fullWidth
              label={`Username for ${selectedRegistration.player2Name}`}
              value={player2Username}
              onChange={(e) => setPlayer2Username(e.target.value)}
              helperText={`Email: ${selectedRegistration.player2Email || "N/A"}`}
            />
          )}

          {!selectedRegistration?.player1IsNew &&
            !selectedRegistration?.player2IsNew && (
              <Alert severity="info">
                Both players already have accounts. No new usernames needed.
              </Alert>
            )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveDialogOpen(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={processing}
          >
            {processing ? (
              <CircularProgress size={24} />
            ) : (
              "Approve & Create Accounts"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !processing && setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to reject this registration?
          </Typography>
          <TextField
            fullWidth
            label="Rejection Reason (optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={processing}
          >
            {processing ? (
              <CircularProgress size={24} />
            ) : (
              "Reject Registration"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
